export const BIN_KINDS = [
    { colour: 'black', name: 'Refuse', what: 'General rubbish' },
    { colour: 'blue', name: 'Recycling', what: 'Paper, cans, plastic and glass' },
    { colour: 'green', name: 'Garden', what: 'Garden waste' },
];
export const binIds = (c) => ({
    pickup: `sensor.${c}_bin_next_pickup`,
    days: `sensor.${c}_bin_next_pickup_days`,
    needs: `binary_sensor.${c}_bin_needs_putting_out`,
    putOut: `switch.${c}_bin_put_out`,
});
const BIN_ENTITY = /^(sensor|binary_sensor|switch)\.(black|blue|green)_bin_(next_pickup|next_pickup_days|needs_putting_out|put_out)$/;
/** Entities the bins dashboard owns (left out of the Garden room page while the module is on). */
export const isBinEntity = (id) => BIN_ENTITY.test(id);
/** Bin colours this home has (any of the bin's entities present). */
export function binsIn(entities) {
    if (!entities)
        return [];
    return BIN_KINDS.map((k) => k.colour).filter((c) => {
        const ids = binIds(c);
        return !!(entities[ids.pickup] || entities[ids.putOut] || entities[ids.needs]);
    });
}
// ---------------------------------------------------------------- dates
const DAY = 86_400_000;
const ymdFmt = new Map();
/** Calendar date (UTC midnight epoch ms) of an instant in `timeZone`. */
function civilDay(t, timeZone) {
    let f = ymdFmt.get(timeZone);
    if (!f)
        ymdFmt.set(timeZone, (f = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })));
    const p = Object.fromEntries(f.formatToParts(t).map((x) => [x.type, x.value]));
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day));
}
/** Whole calendar days from `now` to `t` in the home's timezone (0 = today, 1 = tomorrow, -1 = yesterday). */
export const daysUntil = (t, now, timeZone) => Math.round((civilDay(t, timeZone) - civilDay(now, timeZone)) / DAY);
/** "Wed 7 Oct" in the home's timezone. */
export const shortDate = (t, timeZone) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone }).format(t);
/**
 * How a collection reads: Today / Tomorrow / "Wed 7 Oct", with "in 3 days" alongside a date
 * (and the date alongside Today/Tomorrow). A pickup already in the past reads as "Was Wed 7 Oct".
 */
export function collectionWords(t, now, timeZone) {
    const days = daysUntil(t, now, timeZone);
    const date = shortDate(t, timeZone);
    if (days < 0)
        return { day: `Was ${date}`, rel: days === -1 ? 'yesterday' : `${-days} days ago`, days };
    if (days === 0)
        return { day: 'Today', rel: date, days };
    if (days === 1)
        return { day: 'Tomorrow', rel: date, days };
    return { day: date, rel: `in ${days} days`, days };
}
/** Local hour (home timezone) from which a bin due tomorrow becomes "Put out tonight". */
export const EVENING_HOUR = 18;
const hmFmt = new Map();
/** Minutes past local midnight in `timeZone`. */
export function localMinutes(t, timeZone) {
    let f = hmFmt.get(timeZone);
    if (!f)
        hmFmt.set(timeZone, (f = new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' })));
    const p = Object.fromEntries(f.formatToParts(t).map((x) => [x.type, x.value]));
    return Number(p.hour) * 60 + Number(p.minute);
}
/** It's EVENING_HOUR or later (home time). */
export const isEvening = (now, timeZone) => localMinutes(now, timeZone) >= EVENING_HOUR * 60;
/** Epoch ms of the next EVENING_HOUR:00 (home time) after `now` (for a re-render / Live Activity start). */
export function nextEvening(now, timeZone) {
    // Step to the top of the next minute, then add the minutes to 18:00 (DST: off by ≤1 h only across a change day,
    // re-checked by the caller's next tick).
    const mins = localMinutes(now, timeZone);
    const target = EVENING_HOUR * 60;
    const ahead = mins < target ? target - mins : 24 * 60 - mins + target;
    const t = Math.floor(now / 60_000) * 60_000 + ahead * 60_000;
    // Correct for a DST shift between now and t.
    const drift = localMinutes(t, timeZone) - target;
    return drift === 0 ? t : t - (((drift + 720 + 1440) % 1440) - 720) * 60_000;
}
const ok = (s) => !!s && s.s !== 'unavailable' && s.s !== 'unknown' && s.s !== '';
export function binView(colour, get, now, timeZone) {
    const kind = BIN_KINDS.find((k) => k.colour === colour);
    const ids = binIds(colour);
    const p = get(ids.pickup);
    const parsed = ok(p) ? Date.parse(p.s) : NaN;
    const pickup = Number.isFinite(parsed) ? parsed : null;
    const words = pickup !== null ? collectionWords(pickup, now, timeZone) : null;
    const needs = get(ids.needs);
    const sw = get(ids.putOut);
    const needsOut = needs?.s === 'on';
    const isOut = sw?.s === 'on';
    const due = !words || words.days <= 0 ? 'now' : words.days === 1 ? (isEvening(now, timeZone) ? 'tonight' : 'tomorrow') : 'later';
    const status = isOut ? 'out' : needsOut ? due : pickup === null ? 'unknown' : 'later';
    return { colour, name: kind.name, what: kind.what, ids, pickup, words, needsOut, isOut, switchMissing: !ok(sw), status };
}
const RANK = { now: 0, tonight: 1, tomorrow: 2, out: 3, later: 4, unknown: 5 };
/** Most urgent first: needs putting out (today, then tonight), due tomorrow, out for collection, then by next pickup. */
export function sortBins(list) {
    return [...list].sort((a, b) => RANK[a.status] - RANK[b.status] || (a.pickup ?? Infinity) - (b.pickup ?? Infinity) || BIN_KINDS.findIndex((k) => k.colour === a.colour) - BIN_KINDS.findIndex((k) => k.colour === b.colour));
}
export const needsAction = (b) => b.status === 'now' || b.status === 'tonight';
export const STATUS_LABEL = {
    now: 'Put out now',
    tonight: 'Put out tonight',
    tomorrow: 'Collection tomorrow',
    out: 'Out for collection',
    later: 'Nothing to do',
    unknown: 'No date yet',
};
/** "Refuse bin out tonight" / "Refuse & Recycling out tonight" for the home-screen chip. */
export function chipText(due) {
    if (!due.length)
        return '';
    const when = due.some((b) => b.status === 'now') ? 'now' : 'tonight';
    const names = due.map((b) => b.name);
    if (names.length === 1)
        return `${names[0]} bin out ${when}`;
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]} out ${when}`;
}
/**
 * Upcoming collections grouped by day. The integration only knows each bin's next pickup; later dates are
 * projected fortnightly (West Oxfordshire's cycle) and flagged `estimated`.
 */
export function schedule(bins, now, timeZone, weeks = 6, everyDays = 14) {
    const end = now + weeks * 7 * DAY;
    const byDay = new Map();
    for (const b of bins) {
        if (b.pickup === null)
            continue;
        for (let t = b.pickup, i = 0; t <= end; t += everyDays * DAY, i++) {
            const words = collectionWords(t, now, timeZone);
            if (words.days < 0)
                continue;
            const key = civilDay(t, timeZone);
            let d = byDay.get(key);
            if (!d)
                byDay.set(key, (d = { t, words, bins: [] }));
            d.bins.push({ colour: b.colour, name: b.name, estimated: i > 0 });
        }
    }
    const order = (c) => BIN_KINDS.findIndex((k) => k.colour === c);
    return [...byDay.values()].sort((a, b) => a.t - b.t).map((d) => ({ ...d, bins: d.bins.sort((x, y) => order(x.colour) - order(y.colour)) }));
}
//# sourceMappingURL=bins.js.map